import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  HStack,
  VStack,
  Icon,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tooltip,
  Select,
  Avatar,
  TableContainer,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaSearch,
  FaEye,
  FaFileAlt,
  FaChevronLeft,
  FaChevronRight,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const MotionTr = motion(Tr);

const GlassMorphismCard = ({ children, ...props }) => {
  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.2)",
    "rgba(255, 255, 255, 0.1)",
  );

  return (
    <Box
      bg={cardBg}
      backdropFilter="blur(20px)"
      borderRadius="20px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="xl"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: "20px",
        padding: "1px",
        background: `linear-gradient(135deg, ${borderColor}, transparent)`,
        mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        maskComposite: "subtract",
        pointerEvents: "none",
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    yet_to_be_approved: { colorScheme: "orange", label: "Pending" },
    approved: { colorScheme: "green", label: "Approved" },
    rejected: { colorScheme: "red", label: "Rejected" },
    paid: { colorScheme: "blue", label: "Paid" },
  };

  const config = statusConfig[status] || { colorScheme: "gray", label: status };

  return (
    <Badge
      colorScheme={config.colorScheme}
      variant="subtle"
      borderRadius="full"
      px={3}
      py={1}
      fontSize="xs"
      fontWeight="semibold"
      textTransform="capitalize"
    >
      {config.label}
    </Badge>
  );
};

const TypeBadge = ({ type }) => {
  const typeConfig = {
    property_owner: { colorScheme: "purple", label: "Property Owner" },
    cpo: { colorScheme: "blue", label: "CPO" },
    bco: { colorScheme: "green", label: "BCO" },
    cpo_operated_by_urzza: { colorScheme: "orange", label: "CPO (Urzza)" },
  };

  const config = typeConfig[type] || { colorScheme: "gray", label: type };

  return (
    <Badge
      colorScheme={config.colorScheme}
      variant="outline"
      borderRadius="full"
      px={3}
      py={1}
      fontSize="xs"
      fontWeight="medium"
    >
      {config.label}
    </Badge>
  );
};

export default function PayrunPayoutsTable({
  payouts = [],
  payrunId,
  month,
  year,
  totalGrossAmount,
  totalNetAmount,
  payoutCount,
}) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const headerBg = useColorModeValue("gray.50", "gray.800");
  const cardBg = useColorModeValue("white", "gray.800");

  // Filter and sort payouts
  const filteredPayouts = useMemo(() => {
    let filtered = [...payouts];

    // Apply status filter
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter((payout) => payout.Status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (payout) =>
          payout.PayeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          payout.PayoutRefNo
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payout.BenificiaryName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payout.UserId?.toString().includes(searchTerm),
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (
          sortConfig.key === "NetAmount" ||
          sortConfig.key === "GrossAmount"
        ) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [payouts, statusFilter, searchTerm, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(filteredPayouts.length / pageSize);
  const paginatedPayouts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredPayouts.slice(startIndex, startIndex + pageSize);
  }, [filteredPayouts, currentPage, pageSize]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return FaSort;
    return sortConfig.direction === "asc" ? FaSortUp : FaSortDown;
  };

  const handleViewDetails = (payout) => {
    navigate(`/admin/payouts/payrun/${payrunId}/payout/${payout.Id}`);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    return filteredPayouts.reduce(
      (acc, payout) => ({
        totalPayouts: acc.totalPayouts + 1,
        totalGross: acc.totalGross + (payout.GrossAmount || 0),
        totalNet: acc.totalNet + (payout.NetAmount || 0),
      }),
      { totalPayouts: 0, totalGross: 0, totalNet: 0 },
    );
  }, [filteredPayouts]);

  return (
    <GlassMorphismCard p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header with Search and Filters */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={4}
        >
          <HStack spacing={4} flex={1}>
            <InputGroup maxW={{ base: "100%", md: "400px" }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="brand.500" />
              </InputLeftElement>
              <Input
                placeholder="Search by payee, user ID, or account..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px #23A979",
                }}
              />
            </InputGroup>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              maxW="180px"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              _focus={{
                borderColor: "brand.500",
                boxShadow: "0 0 0 1px #23A979",
              }}
            >
              <option value="all">All Status</option>
              <option value="yet_to_be_approved">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
            </Select>
          </HStack>

          {/* Summary Stats */}
          <HStack spacing={6} display={{ base: "none", lg: "flex" }}>
            <VStack spacing={0} align="end">
              <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                Filtered Payouts
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="brand.500">
                {summaryStats.totalPayouts}
              </Text>
            </VStack>
            <VStack spacing={0} align="end">
              <Text fontSize="xs" color={subTextColor} fontWeight="medium">
                Filtered Net Amount
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="green.500">
                ₹
                {summaryStats.totalNet.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </Text>
            </VStack>
          </HStack>
        </Flex>

        {/* Table */}
        {filteredPayouts.length > 0 ? (
          <>
            <TableContainer
              borderRadius="xl"
              border="1px solid"
              borderColor={borderColor}
            >
              <Table variant="simple">
                <Thead bg={headerBg}>
                  <Tr>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                    >
                      Payee
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                    >
                      Type
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      cursor="pointer"
                      onClick={() => handleSort("GrossAmount")}
                      _hover={{ color: "brand.500" }}
                    >
                      <HStack spacing={1}>
                        <Text>Gross Amount</Text>
                        <Icon as={getSortIcon("GrossAmount")} boxSize={3} />
                      </HStack>
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      cursor="pointer"
                      onClick={() => handleSort("NetAmount")}
                      _hover={{ color: "brand.500" }}
                    >
                      <HStack spacing={1}>
                        <Text>Net Amount</Text>
                        <Icon as={getSortIcon("NetAmount")} boxSize={3} />
                      </HStack>
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      cursor="pointer"
                      onClick={() => handleSort("status")}
                      _hover={{ color: "brand.500" }}
                    >
                      <HStack spacing={1}>
                        <Text>Status</Text>
                        <Icon as={getSortIcon("status")} boxSize={3} />
                      </HStack>
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                    >
                      Account
                    </Th>
                    <Th
                      border="none"
                      color={subTextColor}
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textTransform="uppercase"
                      py={4}
                      textAlign="center"
                    >
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedPayouts.map((payout, index) => (
                    <MotionTr
                      key={payout.Id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                    >
                      <Td border="none" py={4}>
                        <HStack spacing={3}>
                          <Avatar
                            size="sm"
                            name={payout.PayeeName}
                            bg="brand.500"
                            color="white"
                          />
                          <VStack align="start" spacing={0}>
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color={textColor}
                            >
                              {payout.PayeeName || "N/A"}
                            </Text>
                            <Text fontSize="xs" color={subTextColor}>
                              ID: {payout.UserId}
                            </Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td border="none" py={4}>
                        <TypeBadge type={payout.Type} />
                      </Td>
                      <Td border="none" py={4}>
                        <Text fontSize="sm" color={textColor}>
                          ₹
                          {payout.GrossAmount?.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          }) || "0.00"}
                        </Text>
                      </Td>
                      <Td border="none" py={4}>
                        <Text fontSize="sm" fontWeight="bold" color="green.500">
                          ₹
                          {payout.NetAmount?.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          }) || "0.00"}
                        </Text>
                      </Td>
                      <Td border="none" py={4}>
                        <StatusBadge status={payout.Status} />
                      </Td>
                      <Td border="none" py={4}>
                        <VStack align="start" spacing={0}>
                          <Text
                            fontSize="sm"
                            color={textColor}
                            fontWeight="medium"
                          >
                            {payout.BenificiaryName || "N/A"}
                          </Text>
                          <Text fontSize="xs" color={subTextColor}>
                            {payout.BankAccountNo || "N/A"}
                          </Text>
                        </VStack>
                      </Td>
                      <Td border="none" py={4}>
                        <Flex justify="center">
                          <Tooltip label="View Breakdown" placement="top">
                            <IconButton
                              size="sm"
                              variant="ghost"
                              colorScheme="brand"
                              icon={<Icon as={FaEye} />}
                              borderRadius="lg"
                              onClick={() => handleViewDetails(payout)}
                              _hover={{
                                bg: "rgba(35, 169, 121, 0.1)",
                                transform: "scale(1.1)",
                              }}
                              transition="all 0.2s"
                            />
                          </Tooltip>
                        </Flex>
                      </Td>
                    </MotionTr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Flex
              justify="space-between"
              align="center"
              mt={4}
              direction={{ base: "column", md: "row" }}
              gap={4}
            >
              <HStack spacing={2}>
                <Text fontSize="sm" color={subTextColor}>
                  Show
                </Text>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  size="sm"
                  w="80px"
                  borderRadius="lg"
                  borderColor={borderColor}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Select>
                <Text fontSize="sm" color={subTextColor}>
                  of {filteredPayouts.length} entries
                </Text>
              </HStack>

              <HStack spacing={2}>
                <IconButton
                  size="sm"
                  icon={<Icon as={FaChevronLeft} />}
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1}
                  variant="ghost"
                  borderRadius="lg"
                />
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      variant={currentPage === pageNum ? "solid" : "ghost"}
                      colorScheme={currentPage === pageNum ? "brand" : "gray"}
                      borderRadius="lg"
                      minW="40px"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <IconButton
                  size="sm"
                  icon={<Icon as={FaChevronRight} />}
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage === totalPages}
                  variant="ghost"
                  borderRadius="lg"
                />
              </HStack>
            </Flex>
          </>
        ) : (
          <VStack spacing={4} py={12}>
            <Icon
              as={FaFileAlt}
              boxSize={16}
              color={subTextColor}
              opacity={0.5}
            />
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              No payouts found
            </Text>
            <Text fontSize="sm" color={subTextColor}>
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search or filters"
                : "No payout data available for this payrun"}
            </Text>
          </VStack>
        )}
      </VStack>
    </GlassMorphismCard>
  );
}
